/**
 * Posturax Reviews Sticky Component
 * Maneja las reviews sticky en móvil con auto-scroll y animaciones
 */

class PosturaxStickyReviews extends HTMLElement {
  constructor() {
    super();
    
    this.autoScroll = this.dataset.autoScroll === 'true';
    this.scrollSpeed = parseInt(this.dataset.scrollSpeed) || 3000;
    this.showIndicators = this.dataset.showIndicators === 'true';
    
    this.currentIndex = 0;
    this.scrollInterval = null;
    this.isExpanded = false;
    this.isVisible = false;
    
    this.initElements();
    this.init();
  }

  initElements() {
    this.toggleButton = this.querySelector('[data-toggle-sticky]');
    this.stickyContent = this.querySelector('[data-sticky-content]');
    this.stickyReviews = this.querySelector('[data-sticky-reviews]');
    this.indicators = this.querySelectorAll('[data-sticky-indicator]');
    this.reviewItems = this.querySelectorAll('.posturax-reviews-sticky__sticky-review');
    
    this.totalReviews = this.reviewItems.length;
  }

  init() {
    if (this.totalReviews === 0) {
      console.warn('PosturaxStickyReviews: No reviews found');
      return;
    }
    
    this.bindEvents();
    this.setupIntersectionObserver();
    this.updateIndicators();
    
    if (this.autoScroll && this.totalReviews > 1) {
      this.startAutoScroll();
    }
  }

  bindEvents() {
    // Toggle button
    this.toggleButton?.addEventListener('click', () => {
      this.toggleExpanded();
    });
    
    // Indicator clicks
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        this.goToReview(index);
      });
    });
    
    // Touch/swipe support
    this.addTouchSupport();
    
    // Pause auto-scroll on hover/touch
    this.addEventListener('mouseenter', () => this.pauseAutoScroll());
    this.addEventListener('mouseleave', () => this.resumeAutoScroll());
    this.addEventListener('touchstart', () => this.pauseAutoScroll());
    this.addEventListener('touchend', () => this.resumeAutoScroll());
    
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoScroll();
      } else {
        this.resumeAutoScroll();
      }
    });
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.showSticky();
          } else {
            this.hideSticky();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );
    
    // Observe the reviews section
    const reviewsSection = document.querySelector('.posturax-reviews-sticky');
    if (reviewsSection) {
      observer.observe(reviewsSection);
    }
  }

  showSticky() {
    if (!this.isVisible) {
      this.isVisible = true;
      this.classList.add('is-visible');
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('sticky:shown', {
        detail: { component: 'reviews' }
      }));
    }
  }

  hideSticky() {
    if (this.isVisible) {
      this.isVisible = false;
      this.classList.remove('is-visible');
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('sticky:hidden', {
        detail: { component: 'reviews' }
      }));
    }
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      this.classList.add('is-expanded');
      this.pauseAutoScroll();
    } else {
      this.classList.remove('is-expanded');
      this.resumeAutoScroll();
    }
    
    // Dispatch event
    this.dispatchEvent(new CustomEvent('sticky:toggled', {
      detail: { 
        component: 'reviews',
        expanded: this.isExpanded 
      }
    }));
  }

  startAutoScroll() {
    if (this.scrollInterval) return;
    
    this.scrollInterval = setInterval(() => {
      this.nextReview();
    }, this.scrollSpeed);
  }

  pauseAutoScroll() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  resumeAutoScroll() {
    if (this.autoScroll && this.totalReviews > 1 && !this.isExpanded) {
      this.startAutoScroll();
    }
  }

  nextReview() {
    this.currentIndex = (this.currentIndex + 1) % this.totalReviews;
    this.updateDisplay();
    this.updateIndicators();
  }

  goToReview(index) {
    if (index >= 0 && index < this.totalReviews) {
      this.currentIndex = index;
      this.updateDisplay();
      this.updateIndicators();
      
      // Pause auto-scroll briefly when manually navigating
      this.pauseAutoScroll();
      setTimeout(() => {
        this.resumeAutoScroll();
      }, 2000);
    }
  }

  updateDisplay() {
    if (!this.stickyReviews) return;
    
    const translateX = -this.currentIndex * 100;
    this.stickyReviews.style.transform = `translateX(${translateX}%)`;
  }

  updateIndicators() {
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle('is-active', index === this.currentIndex);
    });
  }

  addTouchSupport() {
    let startX = 0;
    let startY = 0;
    let isScrolling = false;
    
    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    }, { passive: true });
    
    this.addEventListener('touchmove', (e) => {
      if (!startX || !startY) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      
      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(currentY - startY);
      
      if (diffX > diffY) {
        isScrolling = true;
        e.preventDefault();
      }
    }, { passive: false });
    
    this.addEventListener('touchend', (e) => {
      if (!isScrolling || !startX) return;
      
      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      
      if (Math.abs(diffX) > 50) { // Minimum swipe distance
        if (diffX > 0) {
          this.nextReview();
        } else {
          this.previousReview();
        }
      }
      
      startX = 0;
      startY = 0;
      isScrolling = false;
    }, { passive: true });
  }

  previousReview() {
    this.currentIndex = this.currentIndex === 0 ? this.totalReviews - 1 : this.currentIndex - 1;
    this.updateDisplay();
    this.updateIndicators();
  }

  // Public API
  getCurrentReview() {
    return this.reviewItems[this.currentIndex];
  }

  getTotalReviews() {
    return this.totalReviews;
  }

  isAutoScrolling() {
    return this.scrollInterval !== null;
  }

  // Cleanup
  disconnectedCallback() {
    this.pauseAutoScroll();
  }
}

// Register the custom element
if (!customElements.get('posturax-sticky-reviews')) {
  customElements.define('posturax-sticky-reviews', PosturaxStickyReviews);
}

// Global functions for external access
window.PosturaxStickyReviews = {
  getStickyReviews() {
    return document.querySelector('posturax-sticky-reviews');
  },
  
  showSticky() {
    const sticky = this.getStickyReviews();
    if (sticky) {
      sticky.showSticky();
    }
  },
  
  hideSticky() {
    const sticky = this.getStickyReviews();
    if (sticky) {
      sticky.hideSticky();
    }
  },
  
  toggleSticky() {
    const sticky = this.getStickyReviews();
    if (sticky) {
      sticky.toggleExpanded();
    }
  }
};

// Auto-initialize and handle page interactions
document.addEventListener('DOMContentLoaded', () => {
  const stickyReviews = document.querySelector('posturax-sticky-reviews');
  
  if (stickyReviews) {
    // Add entrance animation
    stickyReviews.style.opacity = '0';
    stickyReviews.style.transform = 'translateY(100%)';
    
    // Listen for sticky events
    stickyReviews.addEventListener('sticky:shown', () => {
      console.log('Sticky reviews shown');
      
      // Track analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'sticky_reviews_shown', {
          'event_category': 'engagement',
          'event_label': 'reviews'
        });
      }
    });
    
    stickyReviews.addEventListener('sticky:toggled', (event) => {
      console.log('Sticky reviews toggled:', event.detail.expanded);
      
      // Track analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'sticky_reviews_toggled', {
          'event_category': 'engagement',
          'event_label': event.detail.expanded ? 'expanded' : 'collapsed'
        });
      }
    });
  }
});

// Integration with other sticky components
document.addEventListener('sticky:shown', (event) => {
  const { component } = event.detail;
  
  // If another sticky component is shown, hide reviews sticky
  if (component !== 'reviews') {
    const stickyReviews = document.querySelector('posturax-sticky-reviews');
    if (stickyReviews && stickyReviews.isVisible) {
      stickyReviews.hideSticky();
    }
  }
});

// Handle scroll-based visibility
let scrollTimeout;
window.addEventListener('scroll', () => {
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    const stickyReviews = document.querySelector('posturax-sticky-reviews');
    if (stickyReviews) {
      const reviewsSection = document.querySelector('.posturax-reviews-sticky');
      if (reviewsSection) {
        const rect = reviewsSection.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isInView && !stickyReviews.isVisible) {
          stickyReviews.showSticky();
        } else if (!isInView && stickyReviews.isVisible) {
          stickyReviews.hideSticky();
        }
      }
    }
  }, 100);
});

export default PosturaxStickyReviews;
